import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import * as PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { periodPropType } from "../../propTypes";
import PeriodSelector from "../PeriodSelector/PeriodSelector";
import ProgressButton from "../ProgressButton";

const styles = {
    actionButton: {
        display: "inline-flex",
        margin: "10px 0",
    },
    divider: {
        margin: "10px 0",
    },
    instruction: {
        margin: "10px 0",
        fontWeight: "bold",
    },
    periodSelector: {
        margin: "30px 20px 40px",
        zIndex: -1,
    },
    progress: {
        margin: "15px 0 5px",
    },
    select: {
        marginBottom: 10,
        // zIndex: 1,
    },
    stepNumber: {
        display: "none",
    },
};

const reshapeRuns = (runs) => {
    return Object.entries(runs).map(([id, run]) => ({
        value: id,
        label: run.name,
    }));
};

const reshapeCountries = (countries) => {
    return Object.entries(countries)
        .map(([numericCode, country]) => ({
            value: parseInt(numericCode, 10),
            label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
};

const reshapeRegions = (regions) => {
    return regions.map((region, index) => ({
        value: -index - 1,
        label: region,
    }));
};

const ViewRunDrawerContent = (props) => {
    const { t } = useTranslation();

    const runs = reshapeRuns(props.runs);
    const countries = reshapeCountries(props.countries);
    const regions = reshapeRegions(props.regions);
    const countriesAndRegions = countries.concat(regions);

    const selectedRegion = regions.find((region) => region.label === props.selectedExistingRegion);
    const selectedRegionValue = selectedRegion && selectedRegion.value;

    const selectedRunValue = runs.filter(({ value }) => value === props.selectedExistingRunId);
    const selectedPopulationValue = countriesAndRegions.filter(
        ({ value }) => value === (selectedRegionValue || props.selectedExistingCountryNumericCode)
    );

    return (
        <>
            {props.loadingData && (
                <div style={styles.progress}>
                    <LinearProgress />
                </div>
            )}

            <>
                <div style={styles.instruction}>{t("Select existing run")}</div>

                <Select
                    placeholder={t("Select run...")}
                    options={runs}
                    value={selectedRunValue}
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            ...styles.select,
                        }),
                    }}
                    onChange={({ value }) => props.onSelectRun(value)}
                />
            </>

            {/* <Divider style={styles.divider} /> */}

            <>
                <div style={styles.instruction}>{t("Select country/region to view")}</div>

                <Select
                    placeholder={t("Select country/region...")}
                    options={countriesAndRegions}
                    value={selectedPopulationValue}
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            ...styles.select,
                        }),
                    }}
                    onChange={({ value }) => {
                        if (value >= 0) {
                            props.onSelectCountry(value);
                        } else {
                            props.onSelectRegion(regions.find((region) => region.value === value).label);
                        }
                    }}
                />
            </>

            {/* <Divider style={styles.divider} /> */}

            <div style={{ marginTop: "10px" }}>
                <ProgressButton
                    variant="contained"
                    color="primary"
                    disabled={!(props.selectedExistingCountryNumericCode || props.selectedExistingRegion)}
                    loading={props.loadingRun}
                    style={styles.actionButton}
                    onClick={props.onViewRun}
                >
                    {t("View run")}
                </ProgressButton>
            </div>

            <Divider style={styles.divider} />

            <>
                <div style={styles.instruction}>{t("Select years for display/export of results")}</div>

                <div style={styles.periodSelector}>
                    <PeriodSelector
                        period={props.period}
                        onChange={props.onChangePeriod}
                    />
                </div>
            </>
        </>
    );
};

ViewRunDrawerContent.propTypes = {
    loadingData: PropTypes.bool,
    runs: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    countries: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    regions: PropTypes.arrayOf(PropTypes.string),
    selectedExistingRunId: PropTypes.string,
    selectedExistingCountryNumericCode: PropTypes.number,
    selectedExistingRegion: PropTypes.string,
    period: periodPropType,
    loadingRun: PropTypes.bool,
    onSelectRun: PropTypes.func.isRequired,
    onSelectCountry: PropTypes.func.isRequired,
    onSelectRegion: PropTypes.func.isRequired,
    onViewRun: PropTypes.func.isRequired,
};

ViewRunDrawerContent.defaultProps = {
    loadingData: false,
    runs: {},
    countries: {},
    regions: [],
    loadingRun: false,
};

export default ViewRunDrawerContent;
